// /home/bigbrain/projects/smashparty/src/client/input/emote-wheel.ts
/**
 * Emote-wheel input controller.
 *
 * Manages the open/fire/cancel state machine driven by the gamepad right-stick
 * (radial input) and keyboard 1-6 hotkeys (direct fire).
 *
 * Usage:
 *   const wheel = createEmoteWheelController({ gamepad, ... });
 *   wheel.subscribe((event) => handleEmoteEvent(event));
 *   // In your rAF loop:
 *   wheel.tick();
 */

import type { GamepadSource } from './gamepad';
import { DEADZONE_INNER } from './deadzone';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface EmoteWheelOpenEvent {
  readonly sector: 0 | 1 | 2 | 3 | 4 | 5;
}
export interface EmoteWheelFireEvent {
  readonly emoteIndex: 0 | 1 | 2 | 3 | 4 | 5;
}
export interface EmoteWheelCancelEvent {
  readonly reason: 'deadzone' | 'rate_limit';
}

export type EmoteWheelEvent =
  | { kind: 'open'; payload: EmoteWheelOpenEvent }
  | { kind: 'fire'; payload: EmoteWheelFireEvent }
  | { kind: 'cancel'; payload: EmoteWheelCancelEvent };

export type EmoteWheelListener = (event: EmoteWheelEvent) => void;

export interface EmoteWheelController {
  /** Subscribe to emote-wheel events. Returns unsubscribe. Idempotent. */
  subscribe(listener: EmoteWheelListener): () => void;
  /** Per-frame poll. Reads gamepad right-stick state + manages open/fire/cancel transitions. */
  tick(): void;
  /** Releases keyboard listeners. Idempotent. */
  dispose(): void;
}

/** Sector centre angles in radians. Sector 0 = right (0 rad), sectors clockwise. */
export const EMOTE_WHEEL_SECTOR_ANGLES: readonly [number, number, number, number, number, number] =
  [
    0, // 0: right    — GG
    Math.PI / 3, // 1: upper-right (60°) — Oof
    (2 * Math.PI) / 3, // 2: upper-left (120°) — Taunt
    Math.PI, // 3: left (180°) — Thanks
    -((2 * Math.PI) / 3), // 4: lower-left (-120°) — Cry
    -(Math.PI / 3), // 5: lower-right (-60°) — Cheer
  ] as const;

export interface EmoteWheelControllerDeps {
  gamepad: GamepadSource;
  /** EventTarget for keyboard listeners (1-6 hotkeys). Default: `window`. */
  keyboardTarget?: EventTarget;
  /** Gamepad index to watch. Default: 0. */
  gamepadIndex?: number;
  /** Rate limit in ms — minimum interval between fires. Default: 2000 (1 per 2s, mirrors server). */
  rateLimitMs?: number;
  /** Injectable clock. Default: `() => performance.now()`. */
  now?: () => number;
}

// ---------------------------------------------------------------------------
// computeSector helper
// ---------------------------------------------------------------------------

/**
 * Snaps a (x, y) vector to the nearest of the 6 emote-wheel sectors.
 *
 * Algorithm:
 * 1. Compute angle via atan2(y, x) — returns range -π to π.
 * 2. For each sector angle, compute angular delta wrapped to -π..π.
 * 3. Return the index with the smallest absolute delta.
 */
export function computeSector(x: number, y: number): 0 | 1 | 2 | 3 | 4 | 5 {
  const angle = Math.atan2(y, x);
  let bestIndex = 0;
  let bestDelta = Infinity;

  for (let i = 0; i < EMOTE_WHEEL_SECTOR_ANGLES.length; i++) {
    let delta = angle - EMOTE_WHEEL_SECTOR_ANGLES[i];
    // Wrap delta to [-π, π]
    while (delta > Math.PI) delta -= 2 * Math.PI;
    while (delta < -Math.PI) delta += 2 * Math.PI;
    const absDelta = Math.abs(delta);
    if (absDelta < bestDelta) {
      bestDelta = absDelta;
      bestIndex = i;
    }
  }

  return bestIndex as 0 | 1 | 2 | 3 | 4 | 5;
}

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

type WheelPhase = 'closed' | 'open';

interface WheelState {
  phase: WheelPhase;
  openSector: number;
  lastFireTs: number;
  /** Counts how many consecutive ticks the wheel has been in the 'open' phase.
   * Reset to 0 on close. Used to detect brief flicks (openFrameCount===1 on
   * close → emit cancel{deadzone} instead of fire). */
  openFrameCount: number;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createEmoteWheelController(deps: EmoteWheelControllerDeps): EmoteWheelController {
  const { gamepad, gamepadIndex = 0, rateLimitMs = 2000, now = () => performance.now() } = deps;
  const keyboardTarget = deps.keyboardTarget ?? window;

  const listeners = new Set<EmoteWheelListener>();
  let disposed = false;

  const state: WheelState = {
    phase: 'closed',
    openSector: -1,
    lastFireTs: -Infinity,
    openFrameCount: 0,
  };

  // ---------------------------------------------------------------------------
  // Emit helpers
  // ---------------------------------------------------------------------------

  function emit(event: EmoteWheelEvent): void {
    for (const listener of listeners) {
      listener(event);
    }
  }

  // ---------------------------------------------------------------------------
  // Keyboard handler
  // ---------------------------------------------------------------------------

  function handleKeydown(event: Event): void {
    if (disposed) return;
    const ke = event as KeyboardEvent;
    const match = /^Digit([1-6])$/.exec(ke.code);
    if (!match) return;

    const emoteIndex = (parseInt(match[1], 10) - 1) as 0 | 1 | 2 | 3 | 4 | 5;
    const currentTs = now();

    if (currentTs - state.lastFireTs < rateLimitMs) {
      emit({ kind: 'cancel', payload: { reason: 'rate_limit' } });
      return;
    }

    state.lastFireTs = currentTs;
    emit({ kind: 'fire', payload: { emoteIndex } });
  }

  keyboardTarget.addEventListener('keydown', handleKeydown);

  // ---------------------------------------------------------------------------
  // tick()
  // ---------------------------------------------------------------------------

  function tick(): void {
    if (disposed) return;

    const aim = gamepad.getAim(gamepadIndex);
    const magnitude = Math.hypot(aim.x, aim.y);
    const aboveDeadzone = magnitude > DEADZONE_INNER;

    if (state.phase === 'closed') {
      if (!aboveDeadzone) {
        // Still closed, below deadzone — no-op.
        return;
      }

      // closed → open transition
      const sector = computeSector(aim.x, aim.y);
      state.phase = 'open';
      state.openSector = sector;
      state.openFrameCount = 1; // first tick open
      emit({ kind: 'open', payload: { sector } });
      return;
    }

    // phase === 'open'
    if (aboveDeadzone) {
      const sector = computeSector(aim.x, aim.y);
      if (sector !== state.openSector) {
        // Sector change — emit new open event.
        state.openSector = sector;
        emit({ kind: 'open', payload: { sector } });
      }
      // Accumulate open-tick count (same sector or sector change — still open).
      state.openFrameCount += 1;
      return;
    }

    // open → closed transition (stick released / back into deadzone)
    const closingSector = state.openSector;
    const frameCount = state.openFrameCount;
    state.phase = 'closed';
    state.openSector = -1;
    state.openFrameCount = 0;

    // Deadzone cancel: wheel was open for exactly one tick (brief/accidental flick).
    // The player's stick crossed the deadzone and returned before the next tick
    // had a chance to "confirm" the open state. Suppress fire.
    if (frameCount === 1) {
      emit({ kind: 'cancel', payload: { reason: 'deadzone' } });
      return;
    }

    const currentTs = now();
    if (currentTs - state.lastFireTs < rateLimitMs) {
      emit({ kind: 'cancel', payload: { reason: 'rate_limit' } });
      return;
    }

    state.lastFireTs = currentTs;
    emit({
      kind: 'fire',
      payload: { emoteIndex: closingSector as 0 | 1 | 2 | 3 | 4 | 5 },
    });
  }

  // ---------------------------------------------------------------------------
  // dispose()
  // ---------------------------------------------------------------------------

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    keyboardTarget.removeEventListener('keydown', handleKeydown);
    listeners.clear();
    state.phase = 'closed';
    state.openSector = -1;
    state.openFrameCount = 0;
  }

  // ---------------------------------------------------------------------------
  // subscribe()
  // ---------------------------------------------------------------------------

  function subscribe(listener: EmoteWheelListener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  return { subscribe, tick, dispose };
}
